import { ChevronDown } from '@carbon/icons-react';
import { UpstreamEvent } from 'client';
import React from 'react';
import { useSearchParams } from 'react-router';
import { DefaultSelectFilter } from 'routes/components/default-select-filter';
import { useFilterFormListener } from 'routes/components/filter-form-context';
import { PillButton } from 'routes/components/pill-button';
import { SelectFilterOption } from 'routes/components/select-filter-base';
import { useSingleSelect } from 'routes/hooks/use-select-strategy';

import { getFilterParamKey } from 'DesignSystem/features/table-context/filter-param';
import { Button } from 'DesignSystem/ui/button';
import LongText from 'components/LongText';
import { FilterComponentProps } from 'types/react-table';

import { EVENT_QUEUE_DATE_RANGE_OPTIONS } from '../../utils';

const options = EVENT_QUEUE_DATE_RANGE_OPTIONS;

export function AnnouncedDateFilter(props: FilterComponentProps<UpstreamEvent>) {
  const [searchParams] = useSearchParams();
  if (!props.meta?.filter) return null;
  const paramKey = getFilterParamKey(props.meta.filter.param) ?? '';

  return (
    <AnnouncedDateSelect
      name={paramKey}
      defaultValue={options.find((option) => option.value === searchParams.get(paramKey)) ?? null}
    >
      {({ selectedOption }) => {
        return (
          <Button variant="outline" className="w-full justify-between overflow-hidden">
            <span>{selectedOption ? <LongText text={selectedOption.label} /> : 'All'}</span>
            <ChevronDown />
          </Button>
        );
      }}
    </AnnouncedDateSelect>
  );
}

export function AnnouncedDateFilterPill(props: FilterComponentProps<UpstreamEvent>) {
  const [searchParams] = useSearchParams();
  if (!props.meta?.filter) return null;
  const paramKey = getFilterParamKey(props.meta.filter.param) ?? '';

  return (
    <AnnouncedDateSelect
      name={paramKey}
      defaultValue={options.find((option) => option.value === searchParams.get(paramKey)) ?? null}
      onOpenChange={(open) => {
        if (!open) {
          props.formRef?.current?.requestSubmit();
        }
      }}
    >
      {({ selectedOption }) => (
        <PillButton
          label={props.meta?.label ?? ''}
          selectedOptions={selectedOption ? [selectedOption] : []}
          pinned={props.meta?.filter?.pinned}
          param={paramKey}
        ></PillButton>
      )}
    </AnnouncedDateSelect>
  );
}

function AnnouncedDateSelect(
  props: Pick<React.ComponentProps<typeof DefaultSelectFilter>, 'onOpenChange' | 'onSelect' | 'onInput'> & {
    name: string;
    defaultValue: SelectFilterOption | null;
    children: (props: { selectedOption: SelectFilterOption | null }) => React.ReactNode;
  }
) {
  const [selectedOption, update, reset] = useSingleSelect({
    defaultValue: props.defaultValue ?? undefined,
  });

  useFilterFormListener(
    React.useCallback(
      (event) => {
        if (event.type === 'CLEAR') {
          update(null);
        } else if (event.type === 'RESET') {
          reset();
        }
      },
      [update, reset]
    )
  );

  return (
    <DefaultSelectFilter
      name={props.name}
      value={selectedOption?.value ?? null}
      options={options.map((option) => ({ ...option, keywords: [option.label] }))}
      onSelect={(value) => {
        if (value.trim().length === 0) return;
        const selectedOption = options.find((option) => option.value === value);
        if (!selectedOption) return;

        update(selectedOption);
      }}
      onOpenChange={props.onOpenChange}
    >
      {props.children({ selectedOption: selectedOption ?? null })}
    </DefaultSelectFilter>
  );
}
